import { useEffect, useMemo, useState } from 'react'
import { FlaskConical, Loader2, Lock, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { friendlyErrorMessage } from '@/lib/httpError'
import { presentLisStatus } from '@/lib/lisOrderStatus'
import { useLisCatalog, usePlaceLisOrders } from '@/hooks/useLisOrders'
import { useLisStageAvailability, useLisStages } from '@/hooks/useLisStages'
import { useProcedureProducts } from '@/hooks/useProducts'
import { actionableProducts } from '@/lib/productSupersession'
import type { CreatedProduct } from '@/types/products'
import type { PlaceLisOrdersResult } from '@/api/lisApi'
import type { LisStage, LisStageAvailability, LisStageTestSetup } from '@/api/lisStagesApi'
import {
  chosenProvidersBody,
  EMPTY_PROVIDER,
  noProviderNote,
  providerFromTest,
  providerProblem,
  type ProviderDraft,
} from '@/lib/lis/orderingProvider'

/**
 * MedStar's shape for an order code: letters and digits, at most 20, mixed case.
 * Mirrors the backend so a typo is caught before a round trip, never to narrow
 * what can be ordered.
 */
const TEST_CODE_SHAPE = /^[A-Za-z0-9]{1,20}$/

/** Radix refuses an empty-string item value, so "no stage" needs a sentinel. */
const NO_STAGE = '__none__'

/**
 * The dialog's layout contract, in one place because getting it wrong is what
 * clipped the fields and hid the submit button.
 *
 * `DialogContent` is a GRID with `p-6` and `gap-4`, and its close button is
 * absolutely positioned at `right-4 top-4`. Dropping a scroll area into that
 * gives you a body whose padding comes from a grid cell it does not control —
 * and a focus ring is drawn OUTSIDE a control's box, so a field flush against
 * the scroll edge had its ring cut off on both sides.
 *
 * So the shell is taken over completely: `p-0` removes the grid padding,
 * `flex-col` replaces the grid, and each band owns its own padding. Only the
 * middle band scrolls, which is what keeps the action on screen.
 */
const SHELL = 'flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg'
/** `pr-12` clears the absolutely-positioned close button. */
const BAND_HEADER = 'shrink-0 space-y-1 border-b px-6 py-4 pr-12 text-left'
/** `min-h-0` lets a flex child shrink below its content so it can scroll at all. */
const BAND_BODY = 'min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5'
const BAND_FOOTER = 'shrink-0 gap-2 border-t bg-background px-6 py-4'

const TONE_CLASS = {
  pending: 'border-border/60 bg-muted/40 text-muted-foreground',
  success: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  error: 'border-destructive/40 bg-destructive/10 text-destructive',
  warning: 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-500',
} as const

interface LisOrderDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  stepName?: string
  mrn: string
  procedureId?: string
  formDefinitionId?: string
}

/**
 * **Order test** — order laboratory tests against one product.
 *
 * Stage-first: product, then stage, and the stage brings its own tests
 * pre-selected. Picking a dozen codes by hand every time was the thing this
 * replaces, and the codes a stage orders are predictable.
 *
 * Rules that are easy to undo by accident:
 *
 * - **A stage whose predecessors have no results cannot be chosen**, and the
 *   picker says which stage it is waiting on rather than greying out silently.
 * - **Pre-selected is not forced.** Every test can be unticked and extra codes
 *   typed in, because an exception has to stay possible.
 * - **No stage offers every stage's tests**, unticked — see `unionTests`.
 * - **One order per test code.** The HL7 contract is one test per message, and
 *   the order number is what carries a result back, so a shared one could not
 *   tell two results apart.
 *
 * No DIN is minted here. A laboratory order is placed against a PRODUCT, which
 * already has one; minting a second named a bag that was not the one being
 * tested.
 *
 * This component owns the state and the layout. Each field is its own
 * presentational piece below, so the body reads as the sequence a user walks
 * through rather than three hundred lines of nesting.
 */
export function LisOrderDialog({
  open,
  onOpenChange,
  stepName,
  mrn,
  procedureId,
  formDefinitionId,
}: LisOrderDialogProps) {
  const catalog = useLisCatalog()
  const products = useProcedureProducts(procedureId ?? null, open)
  const stages = useLisStages()
  const placeOrders = usePlaceLisOrders()

  const [productId, setProductId] = useState('')
  const [stageId, setStageId] = useState('')
  const [checked, setChecked] = useState<Set<string>>(new Set())
  const [extras, setExtras] = useState<string[]>([])
  const [draft, setDraft] = useState('')
  const [result, setResult] = useState<PlaceLisOrdersResult | null>(null)
  // The ordering provider typed for each "choose when ordering" test, by code.
  const [providers, setProviders] = useState<Record<string, ProviderDraft>>({})

  const availability = useLisStageAvailability(productId || null, open)

  useEffect(() => {
    if (!open) return
    setProductId('')
    setStageId('')
    setChecked(new Set())
    setExtras([])
    setDraft('')
    setResult(null)
    placeOrders.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const stageList = stages.data ?? []
  const stage = stageList.find((s) => s.id === stageId) ?? null
  const offered = useMemo(() => unionTests(stageList, stage), [stageList, stage])

  // Superseded products are history, not bags (z8nrz7e72t).
  const productOptions = actionableProducts(products.data ?? [])
  const product = productOptions.find((p) => p.id === productId) ?? null

  // Preselect once per opening, when the list arrives. Only into an EMPTY
  // choice, so it never overrides a product someone has already picked.
  useEffect(() => {
    if (!open || productId !== '' || productOptions.length === 0) return
    setProductId(defaultProductId(productOptions))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, products.data])
  const busy = placeOrders.isPending
  // A valid code someone typed but never pressed + on still counts. The + is
  // for queueing SEVERAL codes; forgetting it on the last one must not leave
  // Order Test dead under their cursor with the code sitting right there.
  const committed = [...checked, ...extras]
  const draftCode = draft.trim()
  const pendingCode =
    TEST_CODE_SHAPE.test(draftCode) && !committed.includes(draftCode) ? draftCode : null
  const selected = pendingCode ? [...committed, pendingCode] : committed
  const configured = catalog.data?.configured ?? false

  // Tests whose provider is entered here, among those being ordered. Only with
  // a stage: the backend reads a test's setup from the stage it is ordered at.
  const chooseTests = offered.filter((t) => t.setup?.providerSource === 'choose')
  const chooseCodes = new Set(chooseTests.map((t) => t.testCode))
  const chosenHere = chooseTests.filter((t) => selected.includes(t.testCode))
  const providerInvalid = chosenHere.some((t) => providerProblem(providers[t.testCode] ?? EMPTY_PROVIDER) !== null)
  const canSubmit = !!productId && selected.length > 0 && !busy && !providerInvalid

  // Choosing a stage ticks its defaults. Changing stage starts over rather than
  // carrying the previous stage's ticks onto a different set of tests.
  useEffect(() => {
    setChecked(new Set(offered.filter((t) => t.defaultSelected).map((t) => t.testCode)))
    setExtras([])
    setDraft('')
    // Pre-filled with each test's default provider, for the user to confirm.
    setProviders(
      Object.fromEntries(
        offered
          .filter((t) => t.setup?.providerSource === 'choose')
          .map((t) => [t.testCode, providerFromTest(t.setup ?? {})]),
      ),
    )
  }, [stageId, offered])

  const error = placeOrders.error
    ? friendlyErrorMessage(placeOrders.error, 'The order could not be placed.')
    : null

  const submit = async () => {
    if (!canSubmit || !mrn) return
    setResult(
      await placeOrders.mutateAsync({
        productId,
        mrn,
        testCodes: selected,
        ...(stage ? { stage: stage.name } : {}),
        ...(procedureId ? { procedureId } : {}),
        ...(formDefinitionId ? { formDefinitionId } : {}),
        ...(chosenHere.length > 0
          ? { orderingProviders: chosenProvidersBody(selected, chooseCodes, providers) }
          : {}),
      }),
    )
    setDraft('')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={SHELL}>
        <DialogHeader className={BAND_HEADER}>
          <DialogTitle>Order test</DialogTitle>
          <DialogDescription>
            Order laboratory tests for one product. Results come back on this step.
          </DialogDescription>
        </DialogHeader>

        <div className={BAND_BODY}>
          {catalog.isPending ? (
            <p className='text-sm text-muted-foreground'>Loading…</p>
          ) : !configured ? (
            <p className='text-sm text-muted-foreground'>
              No laboratory system is connected for this tenant. Add a Lab Information System
              integration under Integrations, with its base URL and API key.
            </p>
          ) : result ? (
            <OrderResults result={result} onAgain={() => setResult(null)} />
          ) : (
            <>
              <SectionHeading />

              <ProductField
                products={productOptions}
                value={productId}
                onChange={setProductId}
                disabled={busy}
                isLoading={products.isPending}
                isError={products.isError}
              />

              <StageField
                stages={stageList}
                availability={availability.data ?? []}
                value={stageId}
                onChange={setStageId}
                disabled={busy || !productId}
                showBlocked={!!productId}
              />

              <TestsField
                offered={offered}
                checked={checked}
                onSetAll={(on) =>
                  setChecked(on ? new Set(offered.map((t) => t.testCode)) : new Set())
                }
                onToggle={(code) =>
                  setChecked((prev) => {
                    const next = new Set(prev)
                    if (next.has(code)) next.delete(code)
                    else next.add(code)
                    return next
                  })
                }
                extras={extras}
                onAddExtra={(code) => setExtras((prev) => [...prev, code])}
                onRemoveExtra={(code) => setExtras((prev) => prev.filter((c) => c !== code))}
                draft={draft}
                onDraftChange={setDraft}
                // WITHOUT the pending draft, or the +/duplicate check would see
                // the typed code as already taken and refuse to add it.
                alreadySelected={committed}
                hasStage={!!stage}
                disabled={busy}
              />

              {chosenHere.length > 0 ? (
                <ProvidersField
                  tests={chosenHere}
                  values={providers}
                  onChange={(code, value) => setProviders((prev) => ({ ...prev, [code]: value }))}
                  disabled={busy}
                />
              ) : null}

              {error ? <p className='text-sm text-destructive'>{error}</p> : null}

              <OrderSummary din={product?.din ?? null} mrn={mrn} stepName={stepName} />
            </>
          )}
        </div>

        <DialogFooter className={BAND_FOOTER}>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {configured && !result ? (
            <Button onClick={submit} disabled={!canSubmit}>
              {busy ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' /> Sending…
                </>
              ) : (
                `Order test${selected.length > 1 ? ` (${selected.length})` : ''}`
              )}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Body sections ───────────────────────────────────────────────────────────

function SectionHeading() {
  return (
    <div className='flex items-center gap-2'>
      <FlaskConical className='h-4 w-4 text-muted-foreground' />
      <p className='text-[11px] font-medium uppercase tracking-wider text-muted-foreground'>
        Laboratory order
      </p>
    </div>
  )
}

/** Label + control, so every field in the body is spaced identically. */
function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string
  htmlFor?: string
  hint?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className='space-y-1.5'>
      <div className='flex items-baseline justify-between gap-2'>
        <Label htmlFor={htmlFor} className='text-xs'>
          {label}
        </Label>
        {hint}
      </div>
      {children}
    </div>
  )
}

function ProductField({
  products,
  value,
  onChange,
  disabled,
  isLoading,
  isError,
}: {
  products: CreatedProduct[]
  value: string
  onChange: (id: string) => void
  disabled: boolean
  isLoading: boolean
  isError: boolean
}) {
  return (
    <Field label='Product' htmlFor='lis-product'>
      {isLoading ? (
        <p className='text-sm text-muted-foreground'>Loading products…</p>
      ) : isError ? (
        // Not the same as "no products", and it used to render as if it were.
        // Listing products needs `products.view` while this dialog is reached
        // with `integrations.manage`, so a role holding one and not the other
        // 403s here.
        <p className='text-sm text-destructive'>
          Could not load this procedure&rsquo;s products. This is usually a permissions problem
          rather than an empty list &mdash; viewing products needs the Products permission.
        </p>
      ) : products.length === 0 ? (
        <p className='text-sm text-muted-foreground'>
          This procedure has no products yet, so there is nothing to send to the laboratory. Create
          a product first.
        </p>
      ) : (
        <Select value={value} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger id='lis-product'>
            <SelectValue placeholder='Choose a product…' />
          </SelectTrigger>
          <SelectContent>
            {products.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.din}
                {p.productDescriptionCode ? ` · ${p.productDescriptionCode}` : ''}
                {p.volumeMl != null ? ` · ${p.volumeMl} mL` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </Field>
  )
}

function StageField({
  stages,
  availability,
  value,
  onChange,
  disabled,
  showBlocked,
}: {
  stages: LisStage[]
  availability: LisStageAvailability[]
  value: string
  onChange: (id: string) => void
  disabled: boolean
  showBlocked: boolean
}) {
  const byId = new Map(availability.map((a) => [a.id, a]))
  const blockedReasons = availability.filter((a) => !a.selectable && a.blockedReason)

  return (
    <Field label='Stage' htmlFor='lis-stage'>
      <Select
        value={value === '' ? NO_STAGE : value}
        onValueChange={(v) => onChange(v === NO_STAGE ? '' : v)}
        disabled={disabled}
      >
        <SelectTrigger id='lis-stage'>
          <SelectValue placeholder={disabled ? 'Choose a product first' : 'None'} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_STAGE}>None</SelectItem>
          {stages.map((s) => {
            const avail = byId.get(s.id)
            const blocked = avail ? !avail.selectable : !s.active
            return (
              <SelectItem key={s.id} value={s.id} disabled={blocked}>
                <span className='flex items-center gap-2'>
                  {blocked ? <Lock className='h-3 w-3' /> : null}
                  {s.name}
                </span>
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>

      {/* Say WHY a stage is locked rather than greying it out in silence. */}
      {showBlocked && blockedReasons.length > 0 ? (
        <ul className='space-y-0.5 pt-0.5'>
          {blockedReasons.map((a) => (
            <li key={a.id} className='text-xs text-muted-foreground'>
              {a.blockedReason}
            </li>
          ))}
        </ul>
      ) : null}
    </Field>
  )
}

function TestsField({
  offered,
  checked,
  onSetAll,
  onToggle,
  extras,
  onAddExtra,
  onRemoveExtra,
  draft,
  onDraftChange,
  alreadySelected,
  hasStage,
  disabled,
}: {
  offered: OfferedTest[]
  checked: Set<string>
  /** Tick or untick every offered test at once. */
  onSetAll: (on: boolean) => void
  onToggle: (code: string) => void
  extras: string[]
  onAddExtra: (code: string) => void
  onRemoveExtra: (code: string) => void
  draft: string
  onDraftChange: (value: string) => void
  alreadySelected: string[]
  hasStage: boolean
  disabled: boolean
}) {
  const code = draft.trim()
  const canAdd = TEST_CODE_SHAPE.test(code) && !alreadySelected.includes(code)

  const add = () => {
    if (!canAdd) return
    onAddExtra(code)
    onDraftChange('')
  }

  const allChecked = offered.length > 0 && offered.every((t) => checked.has(t.testCode))

  return (
    <Field
      label='Tests'
      hint={
        hasStage && offered.length > 0 ? (
          // Once a stage is chosen its tests are the list to order from, so
          // taking them all is the common case and one click.
          <button
            type='button'
            className='text-[11px] text-muted-foreground underline hover:text-foreground disabled:opacity-50'
            disabled={disabled}
            onClick={() => onSetAll(!allChecked)}
          >
            {allChecked ? 'Clear all' : 'Select all'}
          </button>
        ) : !hasStage && offered.length > 0 ? (
          <span className='text-[11px] text-muted-foreground'>
            every stage&rsquo;s tests — pick what you need
          </span>
        ) : null
      }
    >
      {offered.length > 0 ? (
        <ul className='space-y-1'>
          {offered.map((t) => (
            <li key={t.testCode}>
              <label className='flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm'>
                <input
                  type='checkbox'
                  checked={checked.has(t.testCode)}
                  onChange={() => onToggle(t.testCode)}
                  disabled={disabled}
                />
                <span className='font-mono'>{t.testCode}</span>
                {t.setup?.description ? (
                  <span className='truncate text-xs text-muted-foreground'>{t.setup.description}</span>
                ) : null}
                {!hasStage && t.stageNames.length > 0 ? (
                  <span className='ml-auto truncate pl-2 text-[11px] text-muted-foreground'>
                    {t.stageNames.join(', ')}
                  </span>
                ) : null}
              </label>
            </li>
          ))}
        </ul>
      ) : (
        <p className='text-sm text-muted-foreground'>
          No tests are configured on any stage yet. Add the codes below, or configure them under
          Configure &rarr; LIS Stages.
        </p>
      )}

      {extras.length > 0 ? (
        <ul className='flex flex-wrap gap-2 pt-1'>
          {extras.map((c) => (
            <li key={c}>
              <button
                type='button'
                title={`Remove ${c}`}
                className='flex items-center gap-1 rounded-full border border-border/60 bg-muted/40 px-2.5 py-1 font-mono text-xs hover:border-destructive/50 hover:text-destructive'
                onClick={() => onRemoveExtra(c)}
              >
                {c}
                <X className='h-3 w-3' />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className='flex gap-2 pt-1'>
        <Input
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              add()
            }
          }}
          placeholder='Add another code, e.g. CTCC'
          className='font-mono'
          disabled={disabled}
        />
        <Button size='icon' variant='outline' className='shrink-0' onClick={add} disabled={!canAdd || disabled}>
          <Plus className='h-4 w-4' />
        </Button>
      </div>
      {canAdd ? (
        <p className='pt-1 text-[11px] text-muted-foreground'>
          <span className='font-mono'>{code}</span> will be included in the order &mdash; Enter or
          + queues another code.
        </p>
      ) : null}
    </Field>
  )
}

/**
 * The ordering provider for each selected "choose when ordering" test, pre-filled
 * with the test's default (z8nrz7ffvz AC3.3). Epic matches only by NPI, so the
 * NPI is what is checked; the names are optional.
 */
function ProvidersField({
  tests,
  values,
  onChange,
  disabled,
}: {
  tests: OfferedTest[]
  values: Record<string, ProviderDraft>
  onChange: (testCode: string, value: ProviderDraft) => void
  disabled: boolean
}) {
  return (
    <Field label='Ordering provider'>
      <div className='space-y-3'>
        {tests.map((t) => {
          const value = values[t.testCode] ?? EMPTY_PROVIDER
          const set = (key: keyof ProviderDraft, v: string) => onChange(t.testCode, { ...value, [key]: v })
          const problem = providerProblem(value)
          return (
            <div key={t.testCode} className='space-y-1.5 rounded-lg border px-3 py-2'>
              <p className='text-xs'>
                <span className='font-mono'>{t.testCode}</span>
                {t.setup?.description ? <span className='text-muted-foreground'> · {t.setup.description}</span> : null}
              </p>
              <div className='grid grid-cols-2 gap-2'>
                <Input
                  aria-label={`${t.testCode} provider NPI`}
                  value={value.npi}
                  onChange={(e) => set('npi', e.target.value)}
                  placeholder='NPI (10 digits)'
                  inputMode='numeric'
                  className='font-mono'
                  disabled={disabled}
                />
                <Input
                  aria-label={`${t.testCode} provider last name`}
                  value={value.lastName}
                  onChange={(e) => set('lastName', e.target.value)}
                  placeholder='Last name (optional)'
                  disabled={disabled}
                />
                <Input
                  aria-label={`${t.testCode} provider first name`}
                  value={value.firstName}
                  onChange={(e) => set('firstName', e.target.value)}
                  placeholder='First name (optional)'
                  disabled={disabled}
                />
                <Input
                  aria-label={`${t.testCode} provider middle name`}
                  value={value.middleName}
                  onChange={(e) => set('middleName', e.target.value)}
                  placeholder='Middle name (optional)'
                  disabled={disabled}
                />
              </div>
              {problem ? (
                <p className='text-[11px] text-destructive'>{problem}</p>
              ) : !value.npi.trim() ? (
                <p className='text-[11px] text-muted-foreground'>{noProviderNote(t.setup?.blockIfNoProvider)}</p>
              ) : null}
            </div>
          )
        })}
      </div>
    </Field>
  )
}

function OrderSummary({
  din,
  mrn,
  stepName,
}: {
  din: string | null
  mrn: string
  stepName?: string
}) {
  return (
    <dl className='grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 border-t pt-3 text-xs'>
      <dt className='text-muted-foreground'>DIN</dt>
      <dd className='font-mono text-foreground'>{din ?? '—'}</dd>
      <dt className='text-muted-foreground'>MRN</dt>
      <dd className='font-mono text-foreground'>{mrn}</dd>
      <dt className='text-muted-foreground'>Step</dt>
      <dd className='text-foreground'>{stepName ?? '—'}</dd>
    </dl>
  )
}

/** One row per order placed, because each has its own number and its own answer. */
function OrderResults({ result, onAgain }: { result: PlaceLisOrdersResult; onAgain: () => void }) {
  return (
    <div className='space-y-3'>
      {result.placed.map((order) => {
        const presentation = presentLisStatus(order.status)
        return (
          <div
            key={order.id}
            className={cn(
              'space-y-1 rounded-lg border px-3 py-2 text-sm',
              TONE_CLASS[presentation.tone],
            )}
          >
            <p className='font-medium'>
              {order.testCode} — {presentation.label}
            </p>
            <p className='text-xs opacity-90'>{presentation.summary}</p>
            {order.reason ? <p className='text-xs font-medium'>{order.reason}</p> : null}
            {order.providerMissing ? (
              <p className='text-xs font-medium'>Sent without an ordering provider: none was found for this test.</p>
            ) : null}
            <p className='font-mono text-xs'>{order.orderId}</p>
          </div>
        )
      })}

      {result.failed.map((f) => (
        <div
          key={f.testCode}
          className='space-y-1 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive'
        >
          <p className='font-medium'>{f.testCode} — not sent</p>
          <p className='text-xs'>{f.error}</p>
        </div>
      ))}

      <Button size='sm' variant='outline' onClick={onAgain}>
        Order more tests
      </Button>
    </div>
  )
}

// ── Which tests to offer ────────────────────────────────────────────────────

export interface OfferedTest {
  testCode: string
  defaultSelected: boolean
  /** Which stages list it — shown only when no stage is chosen. */
  stageNames: string[]
  /**
   * The chosen stage's description and provider setup for this test. Absent
   * without a stage: the backend applies a test's setup only for the stage it
   * is ordered at, so offering the boxes there would promise what is not sent.
   */
  setup?: LisStageTestSetup
}

/**
 * The tests to offer, for one stage or for none.
 *
 * With a stage: its own active tests, its defaults ticked.
 *
 * Without one: **the union of every active stage's tests, all unticked**. An
 * ad-hoc order still needs the codes to be pickable, and typing them by hand is
 * what the stage lists exist to avoid. Deduplicated by code, since the same
 * test commonly appears under several stages — ordering it twice would reach
 * the laboratory as two requests for one specimen.
 */
/**
 * The product the dialog opens on.
 *
 * One product: that one — there is nothing to choose. Several: the most
 * recently CREATED, by `createdAt`, which is almost always the bag this run just
 * produced. Decided by the timestamp rather than by list position, so a change
 * to how the list is sorted cannot quietly change which bag gets ordered for.
 */
export function defaultProductId(
  products: readonly { id: string; createdAt: string }[],
): string {
  if (products.length === 0) return ''
  return products.reduce((latest, p) =>
    Date.parse(p.createdAt) > Date.parse(latest.createdAt) ? p : latest,
  ).id
}

export function unionTests(stages: readonly LisStage[], stage: LisStage | null): OfferedTest[] {
  if (stage) {
    return stage.testCodes
      .filter((t) => t.active)
      .map((t) => ({
        testCode: t.testCode,
        defaultSelected: t.defaultSelected,
        stageNames: [],
        setup: {
          description: t.description ?? null,
          providerSource: t.providerSource ?? null,
          providerNpi: t.providerNpi ?? null,
          providerLastName: t.providerLastName ?? null,
          providerFirstName: t.providerFirstName ?? null,
          providerMiddleName: t.providerMiddleName ?? null,
          blockIfNoProvider: t.blockIfNoProvider ?? false,
        },
      }))
  }

  const byCode = new Map<string, OfferedTest>()
  for (const s of stages) {
    if (!s.active) continue
    for (const t of s.testCodes) {
      if (!t.active) continue
      const existing = byCode.get(t.testCode)
      if (existing) existing.stageNames.push(s.name)
      // Nothing is pre-ticked without a stage: the point of choosing no stage
      // is that this order is an exception, so every code is a deliberate pick.
      else
        byCode.set(t.testCode, {
          testCode: t.testCode,
          defaultSelected: false,
          stageNames: [s.name],
        })
    }
  }
  return [...byCode.values()].sort((a, b) => a.testCode.localeCompare(b.testCode))
}
